import { useEffect, useRef } from 'react';
import { Alert, Box, Button, Code, Skeleton, Stack, Text } from '@citibox-panel/ui';
import { useReportFailure } from '../../search-shell/hooks/useErrorNotice';
import { addressMismatchError } from '../lib/addressMismatchError';
import type { AddressMismatchReport } from '../lib/addressMismatchRow';
import type { AddressMismatchState } from '../model/addressMismatchFlow';

export interface AddressMismatchAlertProps {
  report: AddressMismatchReport;
  // The layout's flow instance: it survives the skeleton remount of a detail refetch.
  state: AddressMismatchState;
  // The sheet is being read to learn whether this tracking was already reported: no button yet.
  checking: boolean;
  // The tracking is already in the sheet (found by the check, or by a send that held back): no
  // button, only the line that names it.
  alreadyReported: boolean;
  // «Notificar mismatch» — sends straight away, no confirmation step. Absent = the host offers no
  // send: the warning still shows, with no button.
  onReport?: () => void;
  // Fired once a failure has floated to the shell's notice, so the flow can hand the CTA back.
  onFailureReported: () => void;
}

// TODO: i18n — copy en ES hasta Fase 1.
const HINT = 'La dirección de esta entrega no coincide con la del titular.';
const NOTICE_ID = 'address-mismatch';
const CHECKING_LABEL = 'Comprobando si el tracking ya se reportó';
// The size of the small outlined «Notificar mismatch», so the warning does not jump when it lands.
const CTA_PLACEHOLDER = { width: 148, height: 30 };

// «Notificar mismatch»: the warning under the «buzón» that the parcel was stored somewhere other
// than the titular's address, and the one-click report of it to Amazon's sheet.
//
// The copy is a HINT, never a verdict — there are legitimate reasons for the two addresses to differ
// (a parcel sent to the office, to a friend's Citibox), so both are on screen and the agent decides.
// The warning itself is never dismissed: it disappears when the conditions that paint it stop
// holding, which is why the CTA takes MUI's `action` slot instead of the «×».
//
// The button exists only while the tracking has NOT been reported yet: until the check answers, a
// loading placeholder holds its slot; once the tracking is in the sheet the CTA gives way to a line
// that says so, and nothing offers to send it again.
//
// Only the outcomes are local. They are polite `role="status"` lines and not `Alert`s, whose
// `role="alert"` the façade cannot lower; failures float to the shell's notice (ADR 0021).
export function AddressMismatchAlert({
  report,
  state,
  checking,
  alreadyReported,
  onReport,
  onFailureReported,
}: AddressMismatchAlertProps) {
  const { phase } = state;
  const pending = phase === 'pending';
  // The CTA is back under a failure, so retrying is one click — unless the tracking is reported.
  const ctaShown =
    !checking && !alreadyReported && (phase === 'idle' || pending || phase === 'error');
  const ctaRef = useRef<HTMLButtonElement>(null);
  const wasBusy = useRef(false);

  // Code → copy, never Google's text nor the code itself. The notice never takes focus.
  useReportFailure(
    NOTICE_ID,
    phase === 'error' ? addressMismatchError(state.errorCode) : null,
    onFailureReported,
  );

  // The CTA loses focus while it is disabled in flight: hand it back after the re-render that
  // re-enables it (a failure). After a duplicate the CTA is gone, and the status line speaks instead.
  useEffect(() => {
    if ((phase === 'idle' || phase === 'error') && wasBusy.current) ctaRef.current?.focus();
    wasBusy.current = phase === 'pending';
  }, [phase]);

  // By default there is no button: while the check asks the sheet, a placeholder holds its slot, and
  // the button replaces it only once the answer is «not reported».
  const placeholder =
    onReport && checking ? (
      <Box role="status" aria-label={CHECKING_LABEL}>
        <Skeleton variant="rounded" {...CTA_PLACEHOLDER} />
      </Box>
    ) : undefined;

  // ONE loading state covers both calls (the duplicate check, then the append): two spinners in a
  // row read as slowness and invite a second click on a write that cannot be taken back.
  const cta =
    onReport && ctaShown ? (
      <Button
        ref={ctaRef}
        variant="outlined"
        color="neutral"
        size="small"
        sentenceCase
        bold
        loading={pending}
        disabled={pending}
        onClick={onReport}
      >
        Notificar mismatch
      </Button>
    ) : undefined;

  return (
    <Stack spacing={1}>
      <Alert
        variant="notice"
        severity="warning"
        {...(cta || placeholder ? { action: cta ?? placeholder } : {})}
      >
        <Stack spacing={0.5}>
          <Text variant="body2" bold>
            {HINT}
          </Text>
          <Text variant="body2">{`Entregada en: ${report.deliveredAddress}`}</Text>
          <Text variant="body2">{`Dirección del titular: ${report.correctAddress}`}</Text>
        </Stack>
      </Alert>
      {/* Only the tracking, never a date: the sheet's first column is the DELIVERY date, and «ya se
          reportó … el X» would read it as the day of the report. */}
      {phase !== 'success' && alreadyReported && (
        <Box role="status">
          <Text variant="body2" bold>
            El tracking <Code>{report.tracking}</Code> ya se reportó a Amazon.
          </Text>
        </Box>
      )}
      {phase === 'success' && (
        <Box role="status">
          <Text variant="body2" bold>
            {state.row === null ? 'Reporte enviado' : `Reporte enviado — fila ${String(state.row)}`}
          </Text>
        </Box>
      )}
    </Stack>
  );
}
